import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Layout,
  Tree,
  Spin,
  Typography,
  Card,
  Space,
  Divider,
  Tag,
  Tabs,
  Button,
  Modal,
  Form,
  Input,
  ConfigProvider,
  message,
  Dropdown,
  Upload,
} from "antd";
import type { TreeDataNode } from "antd";
import {
  FolderOutlined,
  FolderOpenOutlined,
  ApiOutlined,
  CodeOutlined,
  PlusOutlined,
  EditOutlined,
  FolderAddOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  MoreOutlined,
  ImportOutlined,
  UploadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { CollectionForm } from "./collection-form";
import { RequestExecutor } from "./executor";
import useFetch from "../../../hooks/useFetch";
import { Select } from "antd";
import {
  GetCollectionList,
  AddEditCollections,
  GetSpecificApiDetail,
  AddEditRequest,
  ImportPostmanCollection,
  GetProjectList,
  MoveCollectionContent,
  DeleteCollection,
} from "../../../utils/api.constant";
import { eResultCode } from "../../../utils/enum";

const { Sider, Content } = Layout;
const { Title, Text } = Typography;

export enum eCollectionType {
  COLLECTION = 1,
  FOLDER = 2,
}

export type TCollection = {
  id: string | number;
  projectId?: string | number;
  type: eCollectionType;
  name: string;
  description?: string;
  createdBy?: string | number;
  createdOn?: string;
  parentId?: string | number;
  variables?: { key: string; value: string }[];
  children?: any[];
  apiDetails?: TRequest[];
  item?: any[];
};

export enum eRequestType {
  POST = 1,
  GET = 2,
  PUT = 3,
  DELETE = 4,
  PATCH = 5,
}

export enum eMovementType {
  FOLDER = 1,
  API = 2,
}

export type TRequest = {
  id?: string | number;
  projectId?: string | number;
  collectionId: string | number;
  name: string;
  description?: string;
  requestType: eRequestType;
  requestJson: {
    url: string;
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    headers: { key: string; value: string; enabled: boolean; isSystem?: boolean }[];
    query_params?: { key: string; value: string }[];
    body?: {
      type: "raw" | "form-data" | "x-www-form-urlencoded" | "binary";
      content?: string;
      language?: string;
      form_data?: { key: string; type: "text" | "file"; value: string }[];
    };
    isServerSide?: boolean;
  };
  responseJson?: any;
};

/* Components */
function ApiManager() {
  const { post } = useFetch();
  const [collections, setCollections] = useState<TCollection[]>([]);
  const [requests, setRequests] = useState<TRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [scratchRequest, setScratchRequest] = useState<TRequest | null>(null);
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const [treeHeight, setTreeHeight] = useState<number>(500);

  useEffect(() => {
    if (!treeContainerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect.height > 0) {
          setTreeHeight(entry.contentRect.height);
        }
      }
    });
    observer.observe(treeContainerRef.current);
    return () => observer.disconnect();
  }, [loading]);

  // Resizable sidebar state
  const [siderWidth, setSiderWidth] = useState<number>(() => {
    const saved = localStorage.getItem("api-manager-sider-width");
    return saved ? parseInt(saved, 10) : 300;
  });
  const isResizing = useRef(false);

  const startResizing = useCallback(() => {
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const stopResizing = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = "default";
    document.body.style.userSelect = "auto";
  }, []);

  const resize = useCallback((mouseEvent: MouseEvent) => {
    if (isResizing.current) {
      const newWidth = mouseEvent.clientX;
      if (newWidth >= 200 && newWidth <= 600) {
        setSiderWidth(newWidth);
        localStorage.setItem("api-manager-sider-width", newWidth.toString());
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [resize, stopResizing]);

  // Folder modal state
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [folderForm] = Form.useForm();
  const [folderModalState, setFolderModalState] = useState<{
    collectionId: string;
    parentId: string | null;
    editFolderId: string | null;
  }>({ collectionId: "", parentId: null, editFolderId: null });
  const [folderSaving, setFolderSaving] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formId, setFormId] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const [requestModalVisible, setRequestModalVisible] = useState(false);
  const [addRequestCollectionId, setAddRequestCollectionId] = useState<
    string | null
  >(null);
  const [addReqForm] = Form.useForm();
  const [importing, setImporting] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importForm] = Form.useForm();
  const [projectOptions, setProjectOptions] = useState<
    { value: any; label: string }[]
  >([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    loadCollections();
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await post(GetProjectList, {
        data: { pageSize: -1, currentPage: 1 },
      });
      if (response && response.data) {
        const formatted = response.data.map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        setProjectOptions(formatted);
      }
    } catch (e) {
      console.error("Failed to load projects", e);
    } finally {
      setLoadingProjects(false);
    }
  };

  const parseRequestJson = (req: any): TRequest => {
    if (req.requestJson && typeof req.requestJson === "string") {
      try {
        return {
          ...req,
          requestJson: JSON.parse(req.requestJson),
        };
      } catch (e) {
        console.error("Failed to parse requestJson", e);
      }
    }
    return req;
  };

  const loadCollections = async () => {
    setLoading(true);
    try {
      const response = await post(GetCollectionList, {
        data: { pageSize: -1, currentPage: 1 },
      });
      if (response && response.data) {
        setCollections(response.data);
        // Recursively extract all requests from the collection tree
        const allRequests: TRequest[] = [];
        const extract = (
          nodes: any[],
          parentColId: string | number | null = null,
        ) => {
          nodes.forEach((node) => {
            const currentId = node.id || parentColId;

            if (node.apiDetails) {
              allRequests.push(
                ...node.apiDetails.map((req: any) => ({
                  ...parseRequestJson(req),
                  collectionId: req.collectionId || currentId,
                })),
              );
            }
            if (node.item) {
              allRequests.push(
                ...node.item.map((req: any) => ({
                  ...parseRequestJson(req),
                  collectionId: req.collectionId || currentId,
                })),
              );
            }

            if (node.children) {
              extract(node.children, currentId);
            }
          });
        };
        extract(response.data);
        setRequests(allRequests);

        // Default: Expand all the root collections
        const allK = getAllKeys(response.data);
        setExpandedKeys(allK);
      }
    } catch (error) {
      console.error("Failed to load collections", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRequest = (
    collectionId: string,
    folderId: string | null = null,
  ) => {
    const newReqId = `req_${Date.now()}`;
    const newReq: TRequest = {
      id: newReqId,
      projectId: 1, // defaulting for now
      collectionId: folderId || collectionId,
      name: "Untitled Request",
      requestType: eRequestType.GET,
      requestJson: {
        url: "",
        method: "GET",
        headers: [],
        isServerSide: false,
      },
    };
    setRequests([...requests, newReq]);
    setSelectedKeys([newReqId]);
    setScratchRequest(null);
  };

  const handleNewScratchRequest = () => {
    const newReq: TRequest = {
      id: "scratch",
      collectionId: 0,
      name: "New Request",
      requestType: eRequestType.GET,
      requestJson: {
        url: "",
        method: "GET",
        headers: [],
        isServerSide: false,
      },
    };
    setScratchRequest(newReq);
    setSelectedKeys([]);
  };

  const handleImport = async (values: { projectId: any; file: any }) => {
    setImporting(true);
    try {
      const formData = new FormData();
      const fileToUpload = Array.isArray(values.file)
        ? values.file[0]?.originFileObj || values.file[0]
        : values.file;

      if (!fileToUpload) {
        message.error("Please select a file to import");
        return;
      }

      formData.append("file", fileToUpload);
      formData.append("projectId", String(values.projectId));

      const response = await post(ImportPostmanCollection, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (
        response &&
        response.dataResponse?.returnCode === eResultCode.SUCCESS
      ) {
        message.success(response.dataResponse?.description);
        setImportModalVisible(false);
        importForm.resetFields();
        loadCollections();
      } else {
        message.error(response?.dataResponse?.description || "Import failed");
      }
    } catch (error) {
      console.error("Import error:", error);
      message.error("Error importing collection");
    } finally {
      setImporting(false);
    }
  };

  const openFolderModal = (
    collectionId: string,
    parentId: string | null,
    editFolderId: string | null = null,
    currentName: string = "",
  ) => {
    setFolderModalState({ collectionId, parentId, editFolderId });
    folderForm.setFieldsValue({ name: currentName });
    setIsFolderModalOpen(true);
  };

  const fetchRequestDetails = async (requestId: number | string) => {
    try {
      // If it's a temporary ID, skip fetching
      if (requestId?.toString().startsWith("req_")) return;

      const response = await post(GetSpecificApiDetail, {
        data: { id: requestId },
      });
      if (response && response.data) {
        const fullRequest = parseRequestJson(response.data);
        setRequests((prev) =>
          prev.map((r) =>
            r.id?.toString() === requestId?.toString() ? fullRequest : r,
          ),
        );
      }
    } catch (error) {
      console.error("Failed to fetch request details", error);
    }
  };

  const handleFolderSubmit = async (values: any) => {
    try {
      setFolderSaving(true);
      const parentIdForPayload = folderModalState.parentId || folderModalState.collectionId || 0;
      const payload: any = {
        data: {
          name: values.name,
          projectId: 1, // defaulting to 1 based on example
          parentId: parentIdForPayload,
          type: eCollectionType.FOLDER,
        },
      };

      if (folderModalState.editFolderId) {
        payload.data.id = folderModalState.editFolderId;
      }

      const response = await post(AddEditCollections, payload);
      message.success(
        `Folder ${folderModalState.editFolderId ? "updated" : "created"} successfully`,
      );
      
      const savedData = response?.data || { ...payload.data, id: response?.data?.id || `temp_folder_${Date.now()}` };

      setCollections((prevCollections) => {
        const newCollections = [...prevCollections];
        
        const updateTree = (nodes: TCollection[]) => {
          for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            
            // If editing
            if (folderModalState.editFolderId && node.id?.toString() === folderModalState.editFolderId) {
              nodes[i] = { ...node, name: values.name };
              return true;
            }
            
            // If adding and this is the parent
            if (!folderModalState.editFolderId && node.id?.toString() === parentIdForPayload?.toString()) {
               const newFolder: TCollection = {
                 ...savedData,
                 children: [],
               };
               nodes[i] = {
                 ...node,
                 children: node.children ? [...node.children, newFolder] : [newFolder]
               };
               return true;
            }
            
            if (node.children && node.children.length > 0) {
              if (updateTree(node.children)) return true;
            }
          }
          return false;
        };

        if (!folderModalState.editFolderId && (!parentIdForPayload || Number(parentIdForPayload) === 0)) {
            const newFolder: TCollection = {
                 ...savedData,
                 children: [],
            };
            newCollections.push(newFolder);
        } else {
            updateTree(newCollections);
        }
        
        return newCollections;
      });

      if (!folderModalState.editFolderId && parentIdForPayload) {
        setExpandedKeys((keys) => {
          const parentStr = parentIdForPayload.toString();
          const colKey = `col_${parentStr}`;
          const folderKey = `folder_${parentStr}`;
          if (!keys.includes(colKey) && !keys.includes(folderKey)) {
             return [...keys, colKey, folderKey];
          }
          return keys;
        });
      }

      setIsFolderModalOpen(false);
    } catch (e) {
      message.error("Failed to save folder");
    } finally {
      setFolderSaving(false);
    }
  };

  const getAllKeys = (nodes: TCollection[]): React.Key[] => {
    let keys: React.Key[] = [];
    nodes.forEach((node) => {
      const isCollection = node.type === eCollectionType.COLLECTION;
      keys.push(`${isCollection ? "col_" : "folder_"}${node.id}`);
      if (node.children && node.children.length > 0) {
        keys = [...keys, ...getAllKeys(node.children)];
      }
    });
    return keys;
  };

  const handleExpandAll = () => {
    const allKeys = getAllKeys(collections);
    setExpandedKeys(allKeys);
  };

  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  const handleDelete = (
    id: string,
    name: string,
    isRoot: boolean = false,
    movementType: eMovementType = eMovementType.FOLDER,
  ) => {
    Modal.confirm({
      title: `Are you sure you want to delete ${isRoot ? "the collection" : ""} "${name}"?`,
      icon: <ExclamationCircleOutlined />,
      content: isRoot
        ? "Warning: Deleting a root collection will remove all its folders and requests. This action cannot be undone."
        : "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const response = await post(DeleteCollection, {
            data: { id, requestType: movementType },
          });
          if (
            response &&
            response.dataResponse?.returnCode === eResultCode.SUCCESS
          ) {
            message.success(`${name} deleted successfully`);
            loadCollections();
          } else {
            message.error(
              response?.dataResponse?.message || "Failed to delete",
            );
          }
        } catch (error) {
          console.error("Delete failed", error);
          message.error("An error occurred during deletion");
        }
      },
    });
  };

  const buildTreeNodes = (nodes: TCollection[]): TreeDataNode[] => {
    return nodes
      .map((node) => {
        if (node.item && !node.id) {
          // This is an item container node, we should flatten its items into the parent's children
          return null;
        }

        const isCollection = node.type === eCollectionType.COLLECTION;
        const currentRequests = requests.filter(
          (req) => req.collectionId?.toString() === node.id?.toString(),
        );

        // Find nested items in children that don't have IDs (item container nodes)
        const nestedItemRequests: TRequest[] = [];
        if (node.children) {
          node.children.forEach((child: any) => {
            if (child.item && !child.id) {
              nestedItemRequests.push(
                ...child.item.map((req: any) => ({
                  ...req,
                  collectionId: node.id,
                })),
              );
            }
          });
        }

        const allNodeRequests = [...currentRequests];
        // Only add nested items if they aren't already in requests (to avoid duplicates if we already extracted them)
        nestedItemRequests.forEach((nr) => {
          if (
            !allNodeRequests.some((r) => r.id?.toString() === nr.id?.toString())
          ) {
            allNodeRequests.push(nr);
          }
        });

        // Filter out item-only nodes from standard recursive tree building
        const validChildren = node.children
          ? node.children.filter((c: any) => c.id || c.children)
          : [];

        return {
          title: (
            <div style={{ position: "relative", paddingRight: "24px" }}>
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  maxWidth: "150px",
                }}
                title={node.name}
              >
                {node.name}
              </div>
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "add-folder",
                        label: isCollection ? "Add Folder" : "Add Subfolder",
                        icon: <FolderAddOutlined />,
                        onClick: (e) => {
                          e.domEvent.stopPropagation();
                          openFolderModal(
                            node.id?.toString(),
                            node.id?.toString(),
                          );
                        },
                      },
                      ...(!isCollection
                        ? [
                            {
                              key: "edit-folder",
                              label: "Edit Folder",
                              icon: <EditOutlined />,
                              onClick: (e: any) => {
                                e.domEvent.stopPropagation();
                                openFolderModal(
                                  node.id?.toString(),
                                  node.parentId?.toString() || null,
                                  node.id?.toString(),
                                  node.name,
                                );
                              },
                            },
                          ]
                        : []),
                      {
                        key: "add-request",
                        label: "Add Request",
                        icon: <PlusOutlined />,
                        onClick: (e) => {
                          e.domEvent.stopPropagation();
                          handleAddRequest(node.id?.toString());
                        },
                      },
                      {
                        key: "delete",
                        label: "Delete",
                        danger: true,
                        icon: <DeleteOutlined />,
                        onClick: (e) => {
                          e.domEvent.stopPropagation();
                          handleDelete(
                            node.id.toString(),
                            node.name,
                            isCollection,
                            eMovementType.FOLDER,
                          );
                        },
                      },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <MoreOutlined style={{ cursor: "pointer", color: "#888", marginRight: "10px" }} />
                </Dropdown>
              </div>
            </div>
          ),
          key: `${isCollection ? "col_" : "folder_"}${node.id}`,
          icon: (props: any) =>
            props.expanded ? (
              <FolderOpenOutlined
                style={{ color: isCollection ? "#1677ff" : "#faad14" }}
              />
            ) : (
              <FolderOutlined
                style={{ color: isCollection ? "#1677ff" : "#faad14" }}
              />
            ),
          children: [
            ...(validChildren.length > 0 ? buildTreeNodes(validChildren) : []),
            ...allNodeRequests.map((req) => ({
              title: (
                <div style={{ position: "relative", paddingRight: "24px" }}>
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    title={req.name}
                  >
                    <ApiOutlined style={{ color: "#888", flex: "none" }} />
                    <Tag
                      color={getMethodColor(req.requestType)}
                      style={{
                        border: 0,
                        padding: "0 4px",
                        margin: 0,
                        fontSize: "10px",
                        flex: "none",
                      }}
                    >
                      {getMethodName(req.requestType)}
                    </Tag>
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                        maxWidth: "120px",
                      }}
                    >
                      {req.name}
                    </span>
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: "delete",
                            label: "Delete",
                            danger: true,
                            icon: <DeleteOutlined />,
                            onClick: (e) => {
                              e.domEvent.stopPropagation();
                              if (req.id) {
                                handleDelete(
                                  req.id.toString(),
                                  req.name,
                                  false,
                                  eMovementType.API,
                                );
                              }
                            },
                          },
                        ],
                      }}
                      trigger={["click"]}
                    >
                      <MoreOutlined
                        style={{ cursor: "pointer", color: "#888", marginRight: "10px" }}
                      />
                    </Dropdown>
                  </div>
                </div>
              ),
              key: req.id?.toString(),
              isLeaf: true,
            })),
          ],
        };
      })
      .filter((n) => n !== null) as TreeDataNode[];
  };

  const filteredRequests = useMemo(() => {
    if (!searchValue) return requests;
    const lowerQuery = searchValue.toLowerCase();
    return requests.filter((r) => {
      const nameMatch = r.name.toLowerCase().includes(lowerQuery);
      const urlMatch = r.requestJson?.url?.toLowerCase()?.includes(lowerQuery);
      const methodMatch = getMethodName(r.requestType).toLowerCase().includes(lowerQuery);
      return nameMatch || urlMatch || methodMatch;
    });
  }, [requests, searchValue]);

  const filteredCollections = useMemo(() => {
    if (!searchValue) return collections;

    const filterNodes = (nodes: any[]): any[] => {
      return nodes
        .map((node) => {
          const lowerQuery = searchValue.toLowerCase();
          const nodeMatches = node.name?.toLowerCase().includes(lowerQuery);
          
          const childrenMatched = node.children ? filterNodes(node.children) : [];
          
          // Check if any request within this specific collection/folder matches
          // Note: we use the raw 'requests' here to check membership, 
          // but buildTreeNodes will use filteredRequests for rendering.
          const nodeRequestsMatched = requests.some(r => 
            r.collectionId?.toString() === node.id?.toString() && 
            (r.name.toLowerCase().includes(lowerQuery) || 
             r.requestJson?.url?.toLowerCase()?.includes(lowerQuery) ||
             getMethodName(r.requestType).toLowerCase().includes(lowerQuery))
          );

          if (nodeMatches || childrenMatched.length > 0 || nodeRequestsMatched) {
            return {
              ...node,
              children: childrenMatched,
            };
          }
          return null;
        })
        .filter(Boolean);
    };

    return filterNodes(collections);
  }, [collections, requests, searchValue]);

  // Update effect to expand matching items
  useEffect(() => {
    if (searchValue) {
      const keys = getAllKeys(filteredCollections);
      setExpandedKeys(keys);
    }
  }, [searchValue, filteredCollections]);

  // Use memoized tree data to avoid recreating React elements and tree structure on every render
  const treeData: TreeDataNode[] = useMemo(() => {
    return buildTreeNodes(filteredCollections as TCollection[]);
  }, [filteredCollections, requests]);


  function getMethodName(type: eRequestType) {
    switch (type) {
      case eRequestType.GET:
        return "GET";
      case eRequestType.POST:
        return "POST";
      case eRequestType.PUT:
        return "PUT";
      case eRequestType.DELETE:
        return "DELETE";
      case eRequestType.PATCH:
        return "PATCH";
      default:
        return "GET";
    }
  }

  function getMethodColor(type: eRequestType) {
    switch (type) {
      case eRequestType.GET:
        return "green";
      case eRequestType.POST:
        return "blue";
      case eRequestType.PUT:
        return "orange";
      case eRequestType.DELETE:
        return "red";
      case eRequestType.PATCH:
        return "purple";
      default:
        return "default";
    }
  }

  const selectedNode =
    selectedKeys.length > 0 ? selectedKeys[0]?.toString() : null;

  // Flatten tree to find collections
  const flattenCollections = (nodes: TCollection[]): TCollection[] => {
    let flatted: TCollection[] = [];
    nodes.forEach((n) => {
      flatted.push(n);
      if (n.children) flatted = flatted.concat(flattenCollections(n.children));
    });
    return flatted;
  };
  const flatCols = flattenCollections(collections);

  // tree node key has prefix 'col_' or 'folder_', so slice it
  const selectedOriginalId = selectedNode
    ?.replace("col_", "")
    ?.replace("folder_", "");
  const selectedCollection = flatCols.find(
    (c) => c.id?.toString() === selectedOriginalId,
  );
  const selectedRequest = requests.find(
    (r) => r.id?.toString() === selectedNode,
  );

  const handleSaveRequest = async (updatedReq: TRequest) => {
    try {
      // Ensure new requests send id: 0
      const isNew = updatedReq.id?.toString().startsWith("req_");
      const submittableReq = {
        ...updatedReq,
        id: isNew ? 0 : updatedReq.id,
      };

      const payload = { data: submittableReq };
      const response = await post(AddEditRequest, payload);

      if (
        response &&
        response.dataResponse?.returnCode === eResultCode.SUCCESS
      ) {
        message.success("Request saved successfully!");

        // If it was a new request, we need the ID from the server
        const savedId = response.data?.id || updatedReq.id;
        const finalRequest = { ...updatedReq, id: savedId };

        // Push the object into the tree locally to avoid full tree refresh
        setRequests((prev) => {
          const exists = prev.some((r) => r.id === updatedReq.id);
          if (exists) {
            return prev.map((r) => (r.id === updatedReq.id ? finalRequest : r));
          }
          return [...prev, finalRequest];
        });

        if (savedId) {
          if (isNew) {
            setSelectedKeys([savedId.toString()]);
          }
          fetchRequestDetails(savedId);
        }
      } else {
        message.error(response?.dataResponse?.message || "Failed to save request");
      }
    } catch (e) {
      console.error("Failed to save request", e);
      message.error("An error occurred during save");
    }
  };

  // Find collection variables for current request (recursively up the tree)
  const currentCollectionVariables = useMemo(() => {
    if (!selectedRequest || !flatCols) return [];

    let allVars: { key: string; value: string }[] = [];
    let currentId: string | number | undefined = selectedRequest.collectionId;

    // Traverse up the parent tree to collect variables
    while (currentId !== undefined && currentId !== null && currentId !== 0) {
      const parentNode = flatCols.find(
        (c) => c.id?.toString() === currentId?.toString(),
      );
      if (parentNode) {
        if (parentNode.variables && parentNode.variables.length > 0) {
          // Add variables from this level.
          // Since we go bottom-up, child variables (closer to request)
          // will be added first and take precedence.
          parentNode.variables.forEach((v) => {
            if (!allVars.some((av) => av.key === v.key)) {
              allVars.push(v);
            }
          });
        }
        currentId = parentNode.parentId;
      } else {
        break;
      }
    }
    return allVars;
  }, [selectedRequest, flatCols]);

  const renderContent = () => {
    const activeRequest = scratchRequest || selectedRequest;
    if (activeRequest) {
      return (
        <RequestExecutor
          key={activeRequest.id}
          request={activeRequest}
          onSave={(updatedReq) => {
            if (activeRequest.id === "scratch") {
              message.info(
                "This is a temporary request. Create a collection to save it permanently.",
              );
              return;
            }
            handleSaveRequest(updatedReq);
          }}
          collectionVariables={currentCollectionVariables}
        />
      );
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          color: "#999",
        }}
      >
        <Space direction="vertical" align="center">
          <CodeOutlined style={{ fontSize: 48, color: "#ccc" }} />
          <Text type="secondary">Select a resource to view details</Text>
        </Space>
      </div>
    );
  };

  const handleDrop = async (info: any) => {
    const { dragNode, node, dropToGap } = info;
    const dropKey = node.key.toString();
    const dragKey = dragNode.key.toString();

    let sourceId = dragKey;
    let movementType = eMovementType.API;

    if (sourceId.startsWith("col_") || sourceId.startsWith("folder_")) {
      sourceId = sourceId.replace("col_", "").replace("folder_", "");
      movementType = eMovementType.FOLDER;
    }

    let targetId = dropKey;

    const getParentIdOfNode = (key: string) => {
      const cleanKey = key.replace("col_", "").replace("folder_", "");
      // Check if it's a collection/folder
      const col = flatCols.find((c) => c.id?.toString() === cleanKey);
      if (col) return col.parentId?.toString() || "0";

      // Check if it's a request
      const req = requests.find((r) => r.id?.toString() === cleanKey);
      if (req) return req.collectionId?.toString();

      return "0";
    };

    if (dropToGap) {
      // If dropped in a gap, the target is the parent of the node defining the gap
      targetId = getParentIdOfNode(dropKey) || "0";
    } else {
      // If dropped directly ON a node, that node is the target parent
      if (targetId.startsWith("col_") || targetId.startsWith("folder_")) {
        targetId = targetId.replace("col_", "").replace("folder_", "");
      } else {
        // Dropped onto a request, it should probably go to the request's parent
        targetId = getParentIdOfNode(dropKey) || "0";
      }
    }

    // Defensive check: don't move if source and target are same
    if (sourceId === targetId) return;

    try {
      const response = await post(MoveCollectionContent, {
        data: {
          sourceId,
          targetId: targetId === "0" ? 0 : targetId, // backend might expect 0 for top level
          movementType,
        },
      });

      if (response && response.dataResponse?.returnCode === 0) {
        message.success("Moved successfully");
        loadCollections();
      } else {
        message.error(response?.dataResponse?.description || "Failed to move");
      }
    } catch (err) {
      console.error("Drop error:", err);
      message.error("Error while moving");
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          fontSize: 13,
          controlHeight: 28,
        },
        components: {
          Card: {
            headerHeight: 40,
            paddingLG: 12,
          },
          Table: {
            padding: 8,
          },
        },
      }}
    >
      <style>{`
        .api-manager-tree .ant-tree-node-content-wrapper {
          display: flex !important;
          align-items: center;
          flex-wrap: nowrap !important;
        }
        .api-manager-tree .ant-tree-title {
          flex: 1;
          min-width: 0;
        }
        .api-manager-tree .ant-tree-treenode {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 4px;
        }
        .api-manager-tree .ant-tree-node-content-wrapper {
          border: 1px solid transparent;
          transition: all 0.2s ease;
        }
        .api-manager-tree .ant-tree-node-content-wrapper:hover {
          background-color: #f5f5f5 !important;
        }
        .api-manager-tree .ant-tree-treenode-drop-target .ant-tree-node-content-wrapper {
          background-color: #e6f7ff !important;
          border-color: #91d5ff !important;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
        }
        .api-manager-tree .ant-tree-drop-indicator {
          background-color: #1890ff;
          height: 2px;
        }
        .api-manager-tree .ant-tree-treenode-draggable {
          cursor: grab;
        }
        .api-manager-tree .ant-tree-treenode-draggable:active {
          cursor: grabbing;
        }
        .sider-resizer {
          width: 4px;
          height: 100%;
          cursor: col-resize;
          background-position: center;
          background-repeat: no-repeat;
          z-index: 10;
          border-left: 1px solid #f0f0f0;
          transition: background-color 0.3s;
        }
        .sider-resizer:hover {
          background-color: #1890ff;
        }
        .sider-resizer:active {
          background-color: #096dd9;
        }
      `}</style>
      <Layout
        style={{
          height: "calc(100vh - 64px)",
          background: "#fff",
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        <Sider
          width={siderWidth}
          theme="light"
          style={{
            borderRight: "none",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "16px",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                Collections
              </Title>
              <Space>
                <Button
                  size="small"
                  type="primary"
                  icon={<ImportOutlined />}
                  onClick={() => setImportModalVisible(true)}
                  title="Import Postman Collection"
                >
                  Import
                </Button>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "add-collection",
                        label: "Add collection",
                        icon: <PlusOutlined />,
                        onClick: () => {
                          setFormId(null);
                          setIsFormOpen(true);
                        },
                      },
                      {
                        key: "new-request",
                        label: "New Request",
                        icon: <ApiOutlined />,
                        onClick: handleNewScratchRequest,
                      },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <PlusOutlined
                    style={{ cursor: "pointer", fontSize: 16 }}
                    title="Add..."
                  />
                </Dropdown>
              </Space>
            </div>

            {/* Fixed Search & Controls */}
            {!loading && (
              <div
                style={{
                  padding: "12px 16px 8px 16px",
                  borderBottom: "1px solid #f0f0f0",
                  background: "#fff",
                  flexShrink: 0,
                }}
              >
                <div style={{ marginBottom: "8px" }}>
                  <Input
                    placeholder="Search APIs, URLs or methods..."
                    allowClear
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{
                      borderRadius: "6px",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #e8e8e8",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Button
                    type="text"
                    size="small"
                    style={{
                      padding: 0,
                      height: "auto",
                      fontSize: "11px",
                      color: "#8c8c8c",
                    }}
                    onClick={handleExpandAll}
                  >
                    Expand All
                  </Button>
                  <small style={{ color: "#d9d9d9" }}>|</small>
                  <Button
                    type="text"
                    size="small"
                    style={{
                      padding: 0,
                      height: "auto",
                      fontSize: "11px",
                      color: "#8c8c8c",
                    }}
                    onClick={handleCollapseAll}
                  >
                    Collapse All
                  </Button>
                </div>
              </div>
            )}

            {/* Scrollable Tree Area */}
            <div
              ref={treeContainerRef}
              style={{
                flex: 1,
                overflowY: "hidden", // Let Tree handle scrolling
                overflowX: "hidden",
                padding: "8px",
                minHeight: 0,
              }}
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: 20 }}>
                  <Spin />
                </div>
              ) : (
                <Tree
                  className="api-manager-tree"
                  blockNode
                  showIcon
                  height={treeHeight}
                  virtual={true}
                  expandedKeys={expandedKeys}
                  onExpand={(keys) => setExpandedKeys(keys)}
                  switcherIcon={({ expanded }: any) =>
                    expanded ? <CaretDownOutlined /> : <CaretRightOutlined />
                  }
                  treeData={treeData}
                  draggable={{
                    icon: false,
                    nodeDraggable: (node) =>
                      !node.key.toString().startsWith("col_"),
                  }}
                  onDrop={handleDrop}
                  onSelect={(keys) => {
                    setScratchRequest(null);
                    setSelectedKeys(keys);
                    if (keys.length > 0) {
                      const selectedId = keys[0]?.toString();
                      const isPrefixed =
                        selectedId.startsWith("col_") ||
                        selectedId.startsWith("folder_");

                      if (isPrefixed) {
                        const originalId = selectedId
                          .replace("col_", "")
                          .replace("folder_", "");
                        const collection = flatCols.find(
                          (c) => c.id?.toString() === originalId,
                        );
                        if (collection) {
                          if (collection.type === eCollectionType.COLLECTION) {
                            setFormId(originalId);
                            setIsFormOpen(true);
                          } else {
                            openFolderModal(
                              collection.id?.toString(),
                              collection.parentId?.toString() || null,
                              collection.id?.toString(),
                              collection.name,
                            );
                          }
                        }
                      } else {
                        fetchRequestDetails(selectedId);
                      }
                    }
                  }}
                  selectedKeys={selectedKeys}
                />
              )}
            </div>
          </div>
        </Sider>
        <div className="sider-resizer" onMouseDown={startResizing} />
        <Content
          style={{ padding: "16px", background: "#fafafa", overflow: "auto" }}
        >
          {renderContent()}
        </Content>

        <CollectionForm
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          id={formId}
          initialData={
            formId
              ? flatCols.find((c) => c.id?.toString() === formId)
              : undefined
          }
          onSuccess={(savedData, isEdit) => {
            setIsFormOpen(false);
            setCollections((prev) => {
              if (isEdit) {
                const updateNode = (nodes: TCollection[]): TCollection[] => {
                  return nodes.map(n => {
                    if (n.id?.toString() === savedData.id?.toString()) {
                      return { ...n, ...savedData };
                    }
                    if (n.children) {
                      return { ...n, children: updateNode(n.children) };
                    }
                    return n;
                  });
                };
                return updateNode(prev);
              } else {
                return [...prev, { ...savedData, id: savedData.id || `temp_${Date.now()}`, type: eCollectionType.COLLECTION, children: [] }];
              }
            });
          }}
        />

        <Modal
          title={folderModalState.editFolderId ? "Edit Folder" : "Add Folder"}
          open={isFolderModalOpen}
          onCancel={() => setIsFolderModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={folderForm}
            layout="vertical"
            onFinish={handleFolderSubmit}
          >
            <Form.Item
              name="name"
              label="Folder Name"
              rules={[
                { required: true, message: "Please input the folder name" },
              ]}
            >
              <Input placeholder="e.g. User Management" autoFocus />
            </Form.Item>
            <Form.Item style={{ textAlign: "right", margin: 0 }}>
              <Button
                onClick={() => setIsFolderModalOpen(false)}
                style={{ marginRight: 8 }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={folderSaving}>
                Save
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Import Postman Collection"
          open={importModalVisible}
          onCancel={() => {
            setImportModalVisible(false);
            importForm.resetFields();
          }}
          footer={null}
          destroyOnClose
        >
          <Form
            form={importForm}
            layout="vertical"
            onFinish={handleImport}
            initialValues={{ projectId: undefined }}
          >
            <Form.Item
              name="projectId"
              label="Select Project"
              rules={[{ required: true, message: "Please select a project" }]}
            >
              <Select
                placeholder="Select Project"
                options={projectOptions}
                loading={loadingProjects}
              />
            </Form.Item>

            <Form.Item
              name="file"
              label="Collection File"
              rules={[{ required: true, message: "Please select a file" }]}
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) return e;
                return e && e.fileList;
              }}
            >
              <Upload beforeUpload={() => false} maxCount={1} accept=".json">
                <Button icon={<UploadOutlined />}>Select Postman JSON</Button>
              </Upload>
            </Form.Item>

            <Form.Item style={{ textAlign: "right", margin: 0 }}>
              <Button
                onClick={() => setImportModalVisible(false)}
                style={{ marginRight: 8 }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={importing}>
                Import
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
    </ConfigProvider>
  );
}

export default ApiManager;
